import { useState, useEffect } from 'react';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';
import { useSEO } from '../../utils/seo';
import { useNavigate } from 'react-router-dom';

export default function DrugUsePage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [expandedFaq, setExpandedFaq] = useState<number | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://thornhilldrugmart.com';

  useSEO({
    title: 'Drug Use & Addiction Thornhill | Understanding Substance Abuse | Richmond Hill, Vaughan, North York',
    description: 'Comprehensive guide to drug use and addiction in Thornhill. Learn about prescription drug abuse, illicit drug addiction, opioid dependency, and how substance abuse develops. Expert addiction pharmacy and clinic serving Richmond Hill, Vaughan, and North York. Professional methadone and Suboxone treatment available. Call (905) 709-9985 for confidential help.',
    keywords: 'drug use Thornhill, drug addiction Thornhill, substance abuse Richmond Hill, prescription drug abuse Vaughan, opioid addiction North York, drug dependency Thornhill, illicit drug use, prescription opioid abuse, drug addiction treatment Thornhill, substance use disorder, addiction pharmacy Thornhill, drug abuse help Richmond Hill, opioid dependency treatment, addiction clinic Vaughan, drug addiction recovery Thornhill',
    canonical: `${siteUrl}/drug-use`,
    ogType: 'article',
    schema: {
      "@context": "https://schema.org",
      "@type": "MedicalWebPage",
      "name": "Drug Use & Addiction - Understanding Substance Abuse",
      "description": "Comprehensive information about drug use and addiction, including prescription drug abuse, illicit drug addiction, how dependency develops, and professional treatment options available in Thornhill, Richmond Hill, Vaughan, and North York.",
      "url": `${siteUrl}/drug-use`,
      "about": {
        "@type": "MedicalCondition",
        "name": "Drug Addiction",
        "alternateName": ["Substance Use Disorder", "Drug Dependency", "Opioid Addiction"],
        "description": "A chronic disease characterized by compulsive drug seeking and use despite harmful consequences"
      },
      "mainEntity": {
        "@type": "MedicalBusiness",
        "name": "Thornhill Drug-Mart Addiction Services",
        "telephone": "+1-905-709-9985",
        "address": {
          "@type": "PostalAddress",
          "streetAddress": "8108 Yonge St Unit 3-4",
          "addressLocality": "Thornhill",
          "addressRegion": "ON",
          "postalCode": "L4J 1W4",
          "addressCountry": "CA"
        }
      }
    }
  });

  const drugTypes = [
    {
      icon: 'ri-capsule-line',
      title: 'Prescription Opioids',
      description: 'Medications like oxycodone, hydrocodone, morphine, and codeine prescribed for pain relief. When misused, these drugs can lead to physical dependence and addiction.',
      examples: ['OxyContin', 'Percocet', 'Vicodin', 'Morphine', 'Codeine'],
      risks: 'High potential for addiction, respiratory depression, overdose risk',
      color: 'teal'
    },
    {
      icon: 'ri-syringe-line',
      title: 'Illicit Opioids',
      description: 'Street drugs including heroin and fentanyl that carry extreme overdose risks. These substances are often mixed with unknown additives, making them unpredictable and dangerous.',
      examples: ['Heroin', 'Fentanyl', 'Carfentanil', 'Street Opioids'],
      risks: 'Extremely high overdose risk, unknown potency, contamination',
      color: 'red'
    },
    {
      icon: 'ri-flask-line',
      title: 'Stimulants',
      description: 'Drugs that increase alertness and energy, including cocaine, methamphetamine, and prescription medications like Adderall. Can cause severe psychological dependence.',
      examples: ['Cocaine', 'Methamphetamine', 'Adderall', 'Ritalin'],
      risks: 'Cardiovascular problems, psychosis, severe addiction',
      color: 'orange'
    },
    {
      icon: 'ri-beer-line',
      title: 'Depressants',
      description: 'Substances that slow brain activity, including benzodiazepines and barbiturates. Often prescribed for anxiety or sleep disorders but carry addiction risks.',
      examples: ['Xanax', 'Valium', 'Ativan', 'Klonopin', 'Ambien'],
      risks: 'Physical dependence, dangerous withdrawal, overdose when mixed',
      color: 'blue'
    }
  ];

  const addictionStages = [
    {
      stage: '1',
      title: 'Experimentation',
      description: 'Initial voluntary use, often driven by curiosity, peer pressure, or to cope with stress. The person may not recognize potential risks.',
      warning: 'Even first-time use can be dangerous with potent drugs like fentanyl'
    },
    {
      stage: '2',
      title: 'Regular Use',
      description: 'Drug use becomes more frequent and predictable. The person may use in specific situations or to achieve certain effects.',
      warning: 'Tolerance begins to develop, requiring more of the drug for the same effect'
    },
    {
      stage: '3',
      title: 'Risky Use',
      description: 'Continued use despite negative consequences. May involve using in dangerous situations or neglecting responsibilities.',
      warning: 'Social, legal, or health problems begin to emerge'
    },
    {
      stage: '4',
      title: 'Dependence',
      description: 'Physical and psychological dependence develops. Withdrawal symptoms occur when not using. Drug use becomes necessary to feel normal.',
      warning: 'Withdrawal symptoms can be severe and require medical supervision'
    },
    {
      stage: '5',
      title: 'Addiction',
      description: 'Compulsive drug seeking and use despite devastating consequences. Loss of control over drug use. Life revolves around obtaining and using drugs.',
      warning: 'Professional treatment is essential for recovery at this stage'
    }
  ];

  const warningSignsCategories = [
    {
      category: 'Physical Signs',
      icon: 'ri-heart-pulse-line',
      signs: [
        'Changes in appetite or sleep patterns',
        'Bloodshot eyes or dilated pupils',
        'Sudden weight loss or gain',
        'Deterioration of physical appearance',
        'Unusual smells on breath, body, or clothing',
        'Tremors, slurred speech, or impaired coordination'
      ]
    },
    {
      category: 'Behavioral Changes',
      icon: 'ri-user-line',
      signs: [
        'Increased secrecy or lying',
        'Changes in friend groups',
        'Neglecting responsibilities',
        'Financial problems or stealing',
        'Legal troubles',
        'Risky or impulsive behavior'
      ]
    },
    {
      category: 'Psychological Symptoms',
      icon: 'ri-brain-line',
      signs: [
        'Mood swings or irritability',
        'Anxiety or paranoia',
        'Depression or apathy',
        'Lack of motivation',
        'Poor judgment',
        'Memory problems or confusion'
      ]
    },
    {
      category: 'Social Impact',
      icon: 'ri-team-line',
      signs: [
        'Withdrawal from family and friends',
        'Loss of interest in hobbies',
        'Problems at work or school',
        'Relationship conflicts',
        'Isolation',
        'Defensive about drug use'
      ]
    }
  ];

  const faqs = [
    {
      question: 'What is the difference between drug use and drug addiction?',
      answer: 'Drug use refers to any consumption of drugs, which may be occasional or experimental. Drug addiction (substance use disorder) is a chronic disease characterized by compulsive drug seeking and use despite harmful consequences. Addiction involves changes in brain chemistry that make it difficult to stop using without professional help. Not everyone who uses drugs becomes addicted, but continued use increases the risk.'
    },
    {
      question: 'How does prescription drug addiction develop?',
      answer: 'Prescription drug addiction often begins with legitimate medical use. Over time, tolerance develops, requiring higher doses for the same effect. Physical dependence can occur even when taking medications as prescribed. Risk factors include prolonged use, higher doses, personal or family history of addiction, and using medications in ways other than prescribed (crushing, snorting, or injecting).'
    },
    {
      question: 'What are opioids and why are they so addictive?',
      answer: 'Opioids are a class of drugs that include prescription pain medications (oxycodone, hydrocodone, morphine) and illegal drugs like heroin and fentanyl. They work by binding to opioid receptors in the brain, blocking pain signals and releasing dopamine. This creates feelings of euphoria and relaxation. The brain adapts to repeated opioid exposure, leading to tolerance, dependence, and addiction. Opioids are particularly addictive because they directly affect the brain\'s reward system.'
    },
    {
      question: 'Can you become addicted after just one use?',
      answer: 'While addiction typically develops over time with repeated use, some powerful drugs like fentanyl can cause physical dependence very quickly. Even first-time use can be fatal with potent opioids. Factors affecting addiction risk include the drug type, method of use, individual brain chemistry, genetics, mental health, and environmental factors. Anyone who uses drugs is at risk, but addiction usually develops through a progression of stages.'
    },
    {
      question: 'What is fentanyl and why is it so dangerous?',
      answer: 'Fentanyl is a synthetic opioid that is 50-100 times more potent than morphine. It\'s extremely dangerous because a tiny amount can be lethal, and it\'s often mixed into other drugs without the user\'s knowledge. Street fentanyl is unpredictable in strength and purity. Many overdose deaths involve fentanyl-contaminated drugs. Even experienced drug users cannot detect fentanyl by sight, smell, or taste, making every use potentially fatal.'
    },
    {
      question: 'How do I know if I\'m addicted to drugs?',
      answer: 'Signs of addiction include: inability to stop using despite wanting to, using more than intended, spending significant time obtaining or using drugs, neglecting responsibilities, continuing use despite problems, experiencing withdrawal symptoms when not using, needing more of the drug for the same effect (tolerance), and unsuccessful attempts to quit. If you\'re concerned about your drug use, contact our Thornhill clinic at (905) 709-9985 for a confidential assessment.'
    },
    {
      question: 'What treatment options are available for drug addiction in Thornhill?',
      answer: 'Our Thornhill addiction pharmacy and clinic offers evidence-based treatments including Methadone Maintenance Treatment (MMT) for opioid addiction, Suboxone therapy, medication-assisted treatment for various substance use disorders, medical monitoring and support, counseling referrals, and long-term recovery planning. We serve patients from Thornhill, Richmond Hill, Vaughan, North York, and throughout the Greater Toronto Area. Treatment is individualized based on your specific needs and circumstances.'
    },
    {
      question: 'Is drug addiction a choice or a disease?',
      answer: 'Drug addiction is recognized as a chronic brain disease by medical and scientific communities. While the initial decision to use drugs is voluntary, repeated use changes brain structure and function, making it extremely difficult to stop without help. Addiction affects brain circuits involved in reward, motivation, memory, and impulse control. Like other chronic diseases (diabetes, heart disease), addiction requires ongoing management and treatment. Understanding addiction as a disease reduces stigma and encourages people to seek help.'
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar isScrolled={isScrolled} />
      
      {/* Hero Section */}
      <section className="relative min-h-[70vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=abstract%20medical%20science%20concept%20with%20neural%20pathways%20and%20brain%20chemistry%20visualization%2C%20teal%20and%20white%20colors%2C%20professional%20healthcare%20imagery%2C%20substance%20abuse%20awareness%2C%20addiction%20medicine%20concept%2C%20clean%20modern%20design%2C%20educational%20medical%20illustration%20with%20molecular%20structures&width=1920&height=800&seq=drug-use-hero-001&orientation=landscape"
            alt="Understanding drug use and addiction"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/60 to-black/80"></div>
        </div>

        <div className="relative z-10 w-full px-4 sm:px-6 py-20 sm:py-24">
          <div className="max-w-7xl mx-auto">
            {/* Content */}
            <header className="text-center">
              {/* Badge */}
              <div className="inline-flex items-center space-x-2 bg-teal-600/20 backdrop-blur-md px-4 py-2 rounded-full mb-4">
                <i className="ri-medicine-bottle-line text-teal-300 text-lg"></i>
                <span className="text-teal-100 text-sm font-semibold uppercase tracking-wide">Education</span>
              </div>

              {/* Breadcrumb */}
              <nav className="flex items-center justify-center mb-6" aria-label="Breadcrumb">
                <div className="flex items-center flex-wrap gap-1 sm:gap-2">
                  <button
                    onClick={() => navigate('/')}
                    className="flex items-center text-white/70 hover:text-white transition-colors cursor-pointer text-xs"
                    aria-label="Go to homepage"
                  >
                    <i className="ri-home-4-line text-sm"></i>
                    <span className="ml-1 font-medium">Home</span>
                  </button>
                  <i className="ri-arrow-right-s-line text-white/50 text-sm"></i>
                  <button
                    onClick={() => navigate('/addiction-services')}
                    className="text-white/70 hover:text-white transition-colors cursor-pointer text-xs font-medium"
                  >
                    Addiction Services
                  </button>
                  <i className="ri-arrow-right-s-line text-white/50 text-sm"></i>
                  <span className="text-teal-300 text-xs font-semibold">Drug Use & Addiction</span>
                </div>
              </nav>

              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4 sm:mb-6 leading-tight">
                Understanding <strong>Drug Use</strong> & <strong>Addiction</strong>
              </h1>
              <p className="text-base sm:text-lg md:text-xl text-white/90 mb-8 sm:mb-10 max-w-3xl mx-auto leading-relaxed">
                Learn about prescription and illicit <strong>drug addiction</strong>, how dependency develops, and professional treatment options available in <strong>Thornhill</strong>
              </p>

              {/* CTA Buttons */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-2xl mx-auto">
                <a
                  href="tel:9057099985"
                  className="bg-teal-600 text-white px-6 py-4 rounded-lg text-base font-semibold hover:bg-teal-700 transition-all hover:scale-105 whitespace-nowrap cursor-pointer shadow-lg flex items-center justify-center"
                  aria-label="Call for confidential help"
                >
                  <i className="ri-phone-line mr-2"></i>
                  Get Confidential Help
                </a>
                <a
                  href="/addiction-services"
                  className="bg-white text-gray-900 px-6 py-4 rounded-lg text-base font-semibold hover:bg-gray-100 transition-all hover:scale-105 whitespace-nowrap cursor-pointer shadow-lg flex items-center justify-center"
                  aria-label="View treatment programs"
                >
                  <i className="ri-heart-pulse-line mr-2"></i>
                  View Treatment Programs
                </a>
              </div>
            </header>
          </div>
        </div>
      </section>

      {/* What is Drug Use Section */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
                What is <strong>Drug Use</strong>?
              </h2>
              <div className="space-y-4 text-base sm:text-lg text-gray-700 leading-relaxed">
                <p>
                  <strong>Drug use</strong> refers to the consumption of legal or illegal substances that alter brain function and behavior. This includes prescription medications, over-the-counter drugs, and illicit substances.
                </p>
                <p>
                  While some drug use may be medically necessary or occasional, repeated use can lead to <strong>tolerance</strong> (needing more to achieve the same effect), <strong>dependence</strong> (physical or psychological need), and <strong>addiction</strong> (compulsive use despite harm).
                </p>
                <p>
                  <strong>Substance use disorder</strong> exists on a spectrum from mild to severe. Understanding the progression from use to addiction is crucial for early intervention and successful treatment.
                </p>
              </div>
            </div>
            <div className="w-full h-96 rounded-2xl overflow-hidden shadow-xl">
              <img
                src="https://readdy.ai/api/search-image?query=medical%20professional%20explaining%20brain%20chemistry%20and%20addiction%20science%20with%20anatomical%20models%2C%20educational%20healthcare%20setting%2C%20teal%20and%20white%20colors%2C%20professional%20consultation%20environment%2C%20substance%20abuse%20education%2C%20compassionate%20medical%20care%2C%20modern%20clinic%20interior%20with%20natural%20lighting&width=800&height=600&seq=drug-use-education-001&orientation=landscape"
                alt="Understanding drug use and addiction education"
                className="w-full h-full object-cover object-center"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Types of Drugs */}
      <section className="py-16 sm:py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <header className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Common Types of <strong>Addictive Drugs</strong>
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Understanding different drug categories and their risks
            </p>
          </header>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {drugTypes.map((drug, index) => (
              <article 
                key={index}
                className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all"
              >
                <div className={`w-16 h-16 flex items-center justify-center bg-${drug.color}-100 rounded-xl mb-6`}>
                  <i className={`${drug.icon} text-3xl text-${drug.color}-600`}></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">{drug.title}</h3>
                <p className="text-gray-700 mb-6 leading-relaxed">{drug.description}</p>
                
                <div className="mb-4">
                  <h4 className="font-semibold text-gray-900 mb-2">Common Examples:</h4>
                  <div className="flex flex-wrap gap-2">
                    {drug.examples.map((example, idx) => (
                      <span 
                        key={idx}
                        className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-sm"
                      >
                        {example}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded">
                  <p className="text-sm text-red-800">
                    <strong>Risks:</strong> {drug.risks}
                  </p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Stages of Addiction */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <header className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              How <strong>Drug Addiction</strong> Develops
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Understanding the progression from first use to addiction
            </p>
          </header>

          <div className="space-y-6">
            {addictionStages.map((item, index) => (
              <article 
                key={index}
                className="bg-gradient-to-r from-teal-50 to-white rounded-2xl p-6 sm:p-8 shadow-md hover:shadow-lg transition-all"
              >
                <div className="flex items-start gap-6">
                  <div className="flex-shrink-0 w-16 h-16 bg-teal-600 text-white rounded-full flex items-center justify-center text-2xl font-bold">
                    {item.stage}
                  </div>
                  <div className="flex-grow">
                    <h3 className="text-2xl font-bold text-gray-900 mb-3">{item.title}</h3>
                    <p className="text-gray-700 mb-4 leading-relaxed">{item.description}</p>
                    <div className="bg-amber-50 border-l-4 border-amber-500 p-4 rounded">
                      <p className="text-sm text-amber-800">
                        <i className="ri-alert-line mr-2"></i>
                        <strong>Warning:</strong> {item.warning}
                      </p>
                    </div>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Warning Signs */}
      <section className="py-16 sm:py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <header className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Warning Signs of <strong>Drug Addiction</strong>
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Recognizing the signs can help you or a loved one get help early
            </p>
          </header>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {warningSignsCategories.map((category, index) => (
              <article 
                key={index}
                className="bg-white rounded-2xl p-8 shadow-lg"
              >
                <div className="flex items-center mb-6">
                  <div className="w-12 h-12 flex items-center justify-center bg-teal-100 rounded-lg mr-4">
                    <i className={`${category.icon} text-2xl text-teal-600`}></i>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900">{category.category}</h3>
                </div>
                <ul className="space-y-3">
                  {category.signs.map((sign, idx) => (
                    <li key={idx} className="flex items-start">
                      <i className="ri-checkbox-circle-fill text-teal-600 mr-3 mt-1 flex-shrink-0"></i>
                      <span className="text-gray-700">{sign}</span>
                    </li>
                  ))}
                </ul>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <header className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Frequently Asked <span className="text-teal-600">Questions</span>
            </h2>
            <p className="text-lg text-gray-600">
              Common questions about drug use and addiction
            </p>
          </header>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <article 
                key={index}
                className="bg-gray-50 rounded-xl shadow-md overflow-hidden"
                itemScope
                itemType="https://schema.org/Question"
              >
                <button
                  onClick={() => setExpandedFaq(expandedFaq === index ? null : index)}
                  className="w-full px-6 py-5 flex items-center justify-between text-left hover:bg-gray-100 transition-colors cursor-pointer"
                  aria-expanded={expandedFaq === index}
                  aria-controls={`faq-answer-${index}`}
                >
                  <h3 
                    className="text-lg font-semibold text-gray-900 pr-4"
                    itemProp="name"
                  >
                    {faq.question}
                  </h3>
                  <i className={`ri-arrow-${expandedFaq === index ? 'up' : 'down'}-s-line text-teal-600 text-2xl flex-shrink-0`}></i>
                </button>
                {expandedFaq === index && (
                  <div 
                    id={`faq-answer-${index}`}
                    className="px-6 pb-5"
                    itemScope
                    itemProp="acceptedAnswer"
                    itemType="https://schema.org/Answer"
                  >
                    <p className="text-gray-700 leading-relaxed" itemProp="text">
                      {faq.answer}
                    </p>
                  </div>
                )}
              </article>
            ))}
          </div>
        </div>

        {/* FAQ Schema */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "FAQPage",
              "mainEntity": faqs.map(faq => ({
                "@type": "Question",
                "name": faq.question,
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": faq.answer
                }
              }))
            })
          }}
        />
      </section>

      {/* CTA Section */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-teal-600 to-cyan-700">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
            Get Help for <strong>Drug Addiction</strong> Today
          </h2>
          <p className="text-lg text-white/90 mb-10 leading-relaxed">
            Our <strong>Thornhill addiction pharmacy</strong> and <strong>clinic</strong> provides evidence-based treatment for <strong>drug addiction</strong>. We offer <strong>methadone</strong> and <strong>Suboxone</strong> programs, medical support, and compassionate care. Serving <strong>Richmond Hill</strong>, <strong>Vaughan</strong>, <strong>North York</strong>, and the Greater Toronto Area.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-2xl mx-auto mb-8">
            <a
              href="tel:9057099985"
              className="bg-white text-teal-600 px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-100 transition-all hover:scale-105 whitespace-nowrap cursor-pointer shadow-lg flex items-center justify-center"
              aria-label="Call for help"
            >
              <i className="ri-phone-line mr-2"></i>
              (905) 709-9985
            </a>
            <a
              href="/addiction-services"
              className="bg-teal-800 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-teal-900 transition-all hover:scale-105 whitespace-nowrap cursor-pointer shadow-lg flex items-center justify-center"
              aria-label="View treatment options"
            >
              <i className="ri-heart-pulse-line mr-2"></i>
              Treatment Options
            </a>
          </div>

          <div className="flex items-center justify-center space-x-2 text-white/80">
            <i className="ri-shield-check-line text-xl"></i>
            <p className="text-sm">Confidential and compassionate care</p>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
